import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { GalaxyIcon } from '@/components/icons';
import PublicHeader from '@/components/PublicHeader';
import { 
  Activity, 
  ArrowRight,
  Users,
  Target,
  MessageSquare,
  UserPlus,
  Mail,
  Shield,
  CheckCircle,
  Eye,
  BarChart3,
  Brain,
  Lightbulb,
  Heart,
  Lock,
  Sparkles,
  GitFork,
  Globe,
  UserCheck,
  ClipboardList,
  Layers,
  TrendingUp,
  PieChart,
  Zap,
  ChevronRight,
  ChevronDown,
  MapPin,
  Building2,
  Briefcase,
  UserCircle,
  TrendingDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

// Animated Perception vs Reality Component
const PerceptionRealityCard = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const scenarios = [
    {
      category: 'Location',
      icon: MapPin,
      segments: [
        { name: 'London', reality: 7.8, perception: 6.2, gap: 'Under-recognized', gapType: 'positive' },
        { name: 'New York', reality: 6.5, perception: 8.1, gap: 'Over-estimated', gapType: 'negative' }
      ],
      insight: 'London team needs more visibility; NY expectations exceed delivery'
    },
    {
      category: 'Team',
      icon: Briefcase,
      segments: [
        { name: 'Engineering', reality: 8.4, perception: 5.9, gap: 'Hidden talent', gapType: 'positive' },
        { name: 'Sales', reality: 6.1, perception: 7.8, gap: 'Over-confident', gapType: 'negative' }
      ],
      insight: 'Engineering contributions undervalued; align Sales self-view'
    },
    {
      category: 'Organisation',
      icon: Building2,
      segments: [
        { name: 'Acme Corp', reality: 5.8, perception: 7.2, gap: 'Disconnect', gapType: 'negative' },
        { name: 'Contoso', reality: 8.9, perception: 7.1, gap: 'Humble stars', gapType: 'positive' }
      ],
      insight: 'Acme needs support intervention; celebrate Contoso wins'
    },
    {
      category: 'Involvement',
      icon: UserCircle,
      segments: [
        { name: 'Public', reality: 6.7, perception: 6.9, gap: 'Aligned', gapType: 'neutral' },
        { name: 'Internal', reality: 7.4, perception: 5.2, gap: 'Under-valued', gapType: 'positive' }
      ],
      insight: 'Internal stakeholders feel unheard; increase recognition'
    }
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % scenarios.length);
        setIsAnimating(false);
      }, 300);
    }, 4000);
    
    return () => clearInterval(interval);
  }, []);
  
  const current = scenarios[currentIndex];
  const IconComponent = current.icon;
  
  const getGapColor = (gapType) => {
    if (gapType === 'positive') return 'text-emerald-600 bg-emerald-50';
    if (gapType === 'negative') return 'text-amber-600 bg-amber-50';
    return 'text-blue-600 bg-blue-50';
  };
  
  const getBarColor = (gapType) => {
    if (gapType === 'positive') return 'bg-emerald-500';
    if (gapType === 'negative') return 'bg-amber-500';
    return 'bg-blue-500';
  };
  
  const getDiffIcon = (reality, perception) => {
    if (reality > perception) return <ArrowUp className="w-3 h-3 text-emerald-500" />;
    if (reality < perception) return <ArrowDown className="w-3 h-3 text-amber-500" />;
    return null;
  };

  return (
    <div className="relative bg-white rounded-2xl shadow-2xl p-6 border border-slate-100 overflow-hidden">
      {/* Header */}
      <div className="text-center mb-5">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#5AA2AE] to-[#3d8a95] flex items-center justify-center mx-auto mb-3">
          <Heart className="w-7 h-7 text-white" />
        </div>
        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Perception vs Reality
        </h3>
      </div>
      
      {/* Category Header with Animation */}
      <div className={`flex items-center justify-center gap-2 mb-4 transition-all duration-300 ${isAnimating ? 'opacity-0 transform -translate-y-2' : 'opacity-100 transform translate-y-0'}`}>
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
          <IconComponent className="w-4 h-4 text-slate-600" />
        </div>
        <span className="text-sm font-semibold text-slate-700">{current.category}</span>
      </div>
      
      {/* Two Column Comparison */}
      <div className={`grid grid-cols-2 gap-3 mb-4 transition-all duration-300 ${isAnimating ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'}`}>
        {current.segments.map((segment, idx) => (
          <div key={idx} className="bg-slate-50 rounded-xl p-3">
            {/* Segment Name */}
            <div className="text-center mb-3">
              <span className="text-xs font-bold text-slate-800 uppercase tracking-wide">
                {segment.name}
              </span>
            </div>
            
            {/* Reality Score */}
            <div className="mb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-slate-500 uppercase">Reality</span>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-bold text-slate-700">{segment.reality}</span>
                </div>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#5AA2AE] rounded-full transition-all duration-500"
                  style={{ width: `${segment.reality * 10}%` }}
                />
              </div>
            </div>
            
            {/* Perception Score */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-slate-500 uppercase">Perceived</span>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-bold text-slate-700">{segment.perception}</span>
                </div>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-slate-400 rounded-full transition-all duration-500"
                  style={{ width: `${segment.perception * 10}%` }}
                />
              </div>
            </div>
            
            {/* Gap Indicator */}
            <div className={`flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium ${getGapColor(segment.gapType)}`}>
              {getDiffIcon(segment.reality, segment.perception)}
              <span>{segment.gap}</span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Insight */}
      <div className={`pt-3 border-t border-slate-100 transition-all duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
        <div className="flex items-start gap-2 text-[#5AA2AE]">
          <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span className="text-xs font-medium leading-relaxed">{current.insight}</span>
        </div>
      </div>
      
      {/* Progress Dots */}
      <div className="flex justify-center gap-1.5 mt-4">
        {scenarios.map((_, idx) => (
          <button
            key={idx}
            onClick={() => {
              setIsAnimating(true);
              setTimeout(() => {
                setCurrentIndex(idx);
                setIsAnimating(false);
              }, 150);
            }}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
              idx === currentIndex ? 'bg-[#5AA2AE] w-4' : 'bg-slate-300 hover:bg-slate-400'
            }`}
          />
        ))}
      </div>
      
      {/* Legend */}
      <div className="flex justify-center gap-4 mt-3 text-[10px] text-slate-400">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-[#5AA2AE]" />
          <span>Reality</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-slate-400" />
          <span>Perceived</span>
        </div>
      </div>
    </div>
  );
};

// Animated About Me Questions Component
const AboutMeQuestionsCard = () => {
  const [sliderValue1, setSliderValue1] = useState(70);
  const [sliderValue2, setSliderValue2] = useState(65);
  const [sliderDirection1, setSliderDirection1] = useState(1);
  const [sliderDirection2, setSliderDirection2] = useState(-1);
  const [typedText, setTypedText] = useState('');
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  
  const textExamples = [
    "We are not taking schedule risk seriously....",
    "Contoso can really throw a wrench in our delivery.",
    "The project doesn't seem to make any sense.",
    "It's taking forever to make decisions."
  ];
  
  // Slider animation
  useEffect(() => {
    const interval = setInterval(() => {
      setSliderValue1(prev => {
        const newVal = prev + sliderDirection1 * 0.5;
        if (newVal >= 85) setSliderDirection1(-1);
        if (newVal <= 55) setSliderDirection1(1);
        return Math.max(55, Math.min(85, newVal));
      });
      
      setSliderValue2(prev => {
        const newVal = prev + sliderDirection2 * 0.4;
        if (newVal >= 80) setSliderDirection2(-1);
        if (newVal <= 45) setSliderDirection2(1);
        return Math.max(45, Math.min(80, newVal));
      });
    }, 100);
    
    return () => clearInterval(interval);
  }, [sliderDirection1, sliderDirection2]);
  
  // Typing animation - type text, pause, clear, then start next
  useEffect(() => {
    const currentText = textExamples[textIndex];
    
    if (charIndex < currentText.length) {
      // Still typing
      const timeout = setTimeout(() => {
        setTypedText(currentText.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      }, 50);
      return () => clearTimeout(timeout);
    } else {
      // Finished typing - pause then clear and move to next
      const timeout = setTimeout(() => {
        setTypedText('');
        setCharIndex(0);
        setTextIndex((textIndex + 1) % textExamples.length);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [charIndex, textIndex]);

  return (
    <div className="bg-white rounded-xl shadow-lg p-5 border border-slate-100 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-500">About Me Questions</span>
        <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">Always On</span>
      </div>
      
      {/* Question 1: Overall Sentiment Slider */}
      <div className="bg-slate-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center">
            <TrendingUp className="w-3.5 h-3.5 text-blue-600" />
          </div>
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Overall Sentiment</span>
        </div>
        <p className="text-sm text-slate-600 mb-3">How do you feel this project is going?</p>
        <div className="relative">
          <div className="h-2 bg-gradient-to-r from-red-200 via-amber-200 to-emerald-200 rounded-full" />
          <div 
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-md border-2 border-[#5AA2AE] transition-all duration-100"
            style={{ left: `calc(${sliderValue1}% - 8px)` }}
          />
        </div>
        <div className="flex justify-between mt-1.5 text-[10px] text-slate-400">
          <span>Worst Project Ever</span>
          <span className="font-bold text-[#5AA2AE]">{Math.round(sliderValue1)}%</span>
          <span>Best Project Ever</span>
        </div>
      </div>
      
      {/* Question 2: Unspoken Problem Text */}
      <div className="bg-slate-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded bg-amber-100 flex items-center justify-center">
            <MessageSquare className="w-3.5 h-3.5 text-amber-600" />
          </div>
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Unspoken Problem</span>
        </div>
        <p className="text-sm text-slate-600 mb-3">Is there a problem or issue that we are not discussing openly?</p>
        <div className="bg-white rounded-lg border border-slate-200 p-3 min-h-[60px]">
          <p className="text-sm text-slate-700">
            {typedText}
            <span className="inline-block w-0.5 h-4 bg-[#5AA2AE] ml-0.5 animate-pulse" />
          </p>
        </div>
      </div>
      
      {/* Question 3: Safe to Speak Slider */}
      <div className="bg-slate-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded bg-purple-100 flex items-center justify-center">
            <Shield className="w-3.5 h-3.5 text-purple-600" />
          </div>
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Safe to Speak</span>
        </div>
        <p className="text-sm text-slate-600 mb-3">Do you feel safe to speak up and share an unpopular opinion?</p>
        <div className="relative">
          <div className="h-2 bg-gradient-to-r from-red-200 via-amber-200 to-emerald-200 rounded-full" />
          <div 
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-md border-2 border-[#5AA2AE] transition-all duration-100"
            style={{ left: `calc(${sliderValue2}% - 8px)` }}
          />
        </div>
        <div className="flex justify-between mt-1.5 text-[10px] text-slate-400">
          <span>Not Safe At All</span>
          <span className="font-bold text-[#5AA2AE]">{Math.round(sliderValue2)}%</span>
          <span>Completely Safe</span>
        </div>
      </div>
      
      {/* Anonymity Indicator */}
      <div className="flex items-center justify-center gap-2 pt-2 border-t border-slate-100">
        <Lock className="w-3.5 h-3.5 text-emerald-500" />
        <span className="text-xs text-slate-500">All responses are anonymized</span>
      </div>
    </div>
  );
};

// Animated People Galaxy Component with interactive sliders
const PeopleGalaxyCard = () => {
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [sliderValues, setSliderValues] = useState({ influence: 50, interest: 50, alignment: 50 });
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [containerSize, setContainerSize] = useState({ width: 400, height: 224 });
  const containerRef = React.useRef(null);
  const animationRef = React.useRef(null);
  const intervalRef = React.useRef(null);
  
  // Segment groups with people inside - fixed positions (as percentages)
  const groups = [
    { 
      id: 'engineering', 
      name: 'Engineering', 
      color: 'bg-blue-500',
      colorHex: '#3b82f6',
      pos: { x: 22, y: 25 },
      people: [
        { initials: 'JD', name: 'John D.', influence: 75, interest: 60, alignment: 80 },
        { initials: 'SK', name: 'Sarah K.', influence: 85, interest: 90, alignment: 70 },
        { initials: 'AM', name: 'Alex M.', influence: 45, interest: 70, alignment: 55 },
        { initials: 'BL', name: 'Ben L.', influence: 60, interest: 45, alignment: 85 },
        { initials: 'CW', name: 'Claire W.', influence: 70, interest: 80, alignment: 65 },
      ]
    },
    { 
      id: 'sales', 
      name: 'Sales', 
      color: 'bg-emerald-500',
      colorHex: '#10b981',
      pos: { x: 78, y: 25 },
      people: [
        { initials: 'LT', name: 'Lisa T.', influence: 90, interest: 85, alignment: 75 },
        { initials: 'RC', name: 'Ryan C.', influence: 55, interest: 65, alignment: 80 },
        { initials: 'MH', name: 'Maria H.', influence: 70, interest: 75, alignment: 60 },
        { initials: 'DP', name: 'Dan P.', influence: 65, interest: 50, alignment: 90 },
      ]
    },
    { 
      id: 'leadership', 
      name: 'Leadership', 
      color: 'bg-purple-500',
      colorHex: '#a855f7',
      pos: { x: 22, y: 75 },
      people: [
        { initials: 'MK', name: 'Mike K.', influence: 95, interest: 80, alignment: 85 },
        { initials: 'NP', name: 'Nina P.', influence: 88, interest: 92, alignment: 78 },
        { initials: 'GO', name: 'Grace O.', influence: 82, interest: 70, alignment: 88 },
        { initials: 'HT', name: 'Henry T.', influence: 75, interest: 85, alignment: 72 },
      ]
    },
    { 
      id: 'partners', 
      name: 'Partners', 
      color: 'bg-amber-500',
      colorHex: '#f59e0b',
      pos: { x: 78, y: 75 },
      people: [
        { initials: 'DW', name: 'David W.', influence: 80, interest: 65, alignment: 55 },
        { initials: 'ER', name: 'Emma R.', influence: 60, interest: 75, alignment: 70 },
        { initials: 'TH', name: 'Tom H.', influence: 45, interest: 55, alignment: 80 },
        { initials: 'JB', name: 'James B.', influence: 70, interest: 80, alignment: 65 },
        { initials: 'KL', name: 'Kate L.', influence: 55, interest: 60, alignment: 75 },
        { initials: 'PS', name: 'Peter S.', influence: 85, interest: 70, alignment: 60 },
        { initials: 'AW', name: 'Anna W.', influence: 65, interest: 85, alignment: 90 },
      ]
    },
  ];
  
  // Get container size for accurate positioning
  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    }
  }, []);
  
  // Convert percentage to pixel position
  const toPixels = (percentX, percentY) => ({
    x: (percentX / 100) * containerSize.width,
    y: (percentY / 100) * containerSize.height,
  });
  
  // Calculate person positions around their group
  const getPersonPosition = (group, personIndex, totalPeople) => {
    const angle = (personIndex / totalPeople) * Math.PI * 2 - Math.PI / 2;
    const radius = totalPeople > 5 ? 14 : 12;
    return {
      x: group.pos.x + Math.cos(angle) * radius,
      y: group.pos.y + Math.sin(angle) * radius,
    };
  };
  
  // Animate sliders to target values
  const animateSliders = (person) => {
    const startValues = { ...sliderValues };
    const endValues = { influence: person.influence, interest: person.interest, alignment: person.alignment };
    const duration = 600;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      setSliderValues({
        influence: Math.round(startValues.influence + (endValues.influence - startValues.influence) * easeProgress),
        interest: Math.round(startValues.interest + (endValues.interest - startValues.interest) * easeProgress),
        alignment: Math.round(startValues.alignment + (endValues.alignment - startValues.alignment) * easeProgress),
      });
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    animationRef.current = requestAnimationFrame(animate);
  };
  
  // Auto-play animation
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const groupIds = groups.map(g => g.id);
    let step = 0;
    let currentGroupIndex = 0;
    let currentPersonIndex = 0;
    
    const runAnimation = () => {
      if (!isAutoPlaying) return;
      
      const group = groups[currentGroupIndex];
      
      switch (step) {
        case 0:
          setExpandedGroup(group.id);
          setSelectedPerson(null);
          step = 1;
          break;
        case 1:
          if (group.people.length > 0) {
            const person = group.people[currentPersonIndex];
            setSelectedPerson({ ...person, groupId: group.id });
            animateSliders(person);
          }
          step = 2;
          break;
        case 2:
          currentPersonIndex = (currentPersonIndex + 1) % group.people.length;
          if (group.people.length > 1) {
            const person = group.people[currentPersonIndex];
            setSelectedPerson({ ...person, groupId: group.id });
            animateSliders(person);
          }
          step = 3;
          break;
        case 3:
          setExpandedGroup(null);
          setSelectedPerson(null);
          setSliderValues({ influence: 50, interest: 50, alignment: 50 });
          currentGroupIndex = (currentGroupIndex + 1) % groups.length;
          currentPersonIndex = 0;
          step = 0;
          break;
      }
    };
    
    const initialTimeout = setTimeout(runAnimation, 1500);
    intervalRef.current = setInterval(runAnimation, 2000);
    
    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoPlaying]);
  
  // Stop autoplay and handle user interaction
  const handleUserInteraction = () => {
    if (isAutoPlaying) {
      setIsAutoPlaying(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  };
  
  // Handle group click
  const handleGroupClick = (groupId) => {
    handleUserInteraction();
    setExpandedGroup(prev => prev === groupId ? null : groupId);
    if (expandedGroup === groupId) {
      setSelectedPerson(null);
      setSliderValues({ influence: 50, interest: 50, alignment: 50 });
    }
  };
  
  // Handle person click
  const handlePersonClick = (person, groupId) => {
    handleUserInteraction();
    setSelectedPerson({ ...person, groupId });
    animateSliders(person);
  };
  
  // Center position
  const centerX = 50;
  const centerY = 50;

  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 text-white">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-bold flex items-center gap-2">
          <GalaxyIcon className="w-5 h-5 text-[#5AA2AE]" />
          My People Galaxy
        </h4>
        {!isAutoPlaying && (
          <button 
            onClick={() => setIsAutoPlaying(true)}
            className="text-[10px] px-2 py-1 bg-slate-700 rounded hover:bg-slate-600 transition-colors"
          >
            ▶ Auto-play
          </button>
        )}
      </div>
      <div 
        ref={containerRef}
        className="relative h-56 overflow-hidden"
        style={{ touchAction: 'none' }}
      >
        {/* Lines removed for cleaner visual */}
        
        {/* Central "You" node */}
        <div 
          className="absolute w-14 h-14 rounded-full bg-[#5AA2AE] flex items-center justify-center font-bold text-sm z-20 shadow-lg shadow-[#5AA2AE]/30"
          style={{ 
            left: `${centerX}%`, 
            top: `${centerY}%`, 
            transform: 'translate(-50%, -50%)' 
          }}
        >
          You
        </div>
        
        {/* Group nodes */}
        {groups.map(group => {
          const isExpanded = expandedGroup === group.id;
          
          return (
            <div key={group.id}>
              {/* Group node */}
              <div 
                className="absolute cursor-pointer z-10"
                style={{ 
                  left: `${group.pos.x}%`, 
                  top: `${group.pos.y}%`, 
                  transform: 'translate(-50%, -50%)'
                }}
                onClick={() => handleGroupClick(group.id)}
              >
                <div className={`${group.color} rounded-full flex items-center justify-center font-bold text-xs shadow-lg transition-all duration-300 ${
                  isExpanded ? 'w-8 h-8 ring-2 ring-white/40' : 'w-11 h-11 hover:scale-110'
                }`}>
                  {isExpanded ? '−' : group.people.length}
                </div>
                <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-1 text-[10px] whitespace-nowrap transition-opacity duration-300 ${
                  isExpanded ? 'opacity-60' : 'opacity-100'
                }`}>
                  {group.name}
                </div>
              </div>
              
              {/* Person nodes when expanded */}
              {isExpanded && group.people.map((person, idx) => {
                const personPos = getPersonPosition(group, idx, group.people.length);
                const isSelected = selectedPerson?.initials === person.initials && selectedPerson?.groupId === group.id;
                return (
                  <div
                    key={`${group.id}-${person.initials}`}
                    className="absolute z-10 cursor-pointer transition-all duration-300"
                    style={{ 
                      left: `${personPos.x}%`, 
                      top: `${personPos.y}%`, 
                      transform: 'translate(-50%, -50%)',
                    }}
                    onClick={() => handlePersonClick(person, group.id)}
                  >
                    <div className={`${group.color} w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shadow-md transition-all duration-300 hover:scale-110 ${
                      isSelected ? 'ring-2 ring-white scale-125' : ''
                    }`}>
                      {person.initials}
                    </div>
                    {isSelected && (
                      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[8px] text-white/80 whitespace-nowrap">
                        {person.name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
        
        {/* Status hint */}
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[10px] text-slate-500">
          {selectedPerson ? `Selected: ${selectedPerson.name}` : (isAutoPlaying ? 'Click to interact' : 'Click groups to expand')}
        </div>
      </div>
      
      {/* Sliders section */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        {/* Influence Slider */}
        <div className="bg-slate-700/50 rounded-lg p-3">
          <div className="mb-2">
            <span className="text-[10px] font-semibold text-slate-300 uppercase tracking-wide">Influence</span>
          </div>
          <div className="relative h-2 bg-slate-600 rounded-full overflow-hidden">
            <div 
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full transition-all duration-150"
              style={{ width: `${sliderValues.influence}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-[8px] text-slate-500">
            <span>Low</span>
            <span>High</span>
          </div>
        </div>
        
        {/* Interest Slider */}
        <div className="bg-slate-700/50 rounded-lg p-3">
          <div className="mb-2">
            <span className="text-[10px] font-semibold text-slate-300 uppercase tracking-wide">Interest</span>
          </div>
          <div className="relative h-2 bg-slate-600 rounded-full overflow-hidden">
            <div 
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-150"
              style={{ width: `${sliderValues.interest}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-[8px] text-slate-500">
            <span>Low</span>
            <span>High</span>
          </div>
        </div>
        
        {/* Alignment Slider */}
        <div className="bg-slate-700/50 rounded-lg p-3">
          <div className="mb-2">
            <span className="text-[10px] font-semibold text-slate-300 uppercase tracking-wide">Alignment</span>
          </div>
          <div className="relative h-2 bg-slate-600 rounded-full overflow-hidden">
            <div 
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-400 to-purple-500 rounded-full transition-all duration-150"
              style={{ width: `${sliderValues.alignment}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-[8px] text-slate-500">
            <span>Low</span>
            <span>High</span>
          </div>
        </div>
      </div>
      
      <p className="text-xs text-slate-400 text-center mt-3">
        Your stakeholder network visualization
      </p>
    </div>
  );
};

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <PublicHeader />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-16 px-4 sm:px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#5AA2AE]/10 text-[#5AA2AE] text-sm font-semibold mb-6">
            <Heart className="w-4 h-4" />
            <span>People are everything</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            How Pulse Works
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
            A new way to understand the most critical element of project success — its people.
          </p>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                People are the most important part of any project
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Projects don't fail because of technology or process — they fail because of people. 
                Understanding how your stakeholders truly feel is the key to project success.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Pulse helps you discover new insights and hidden stakeholders, analyze the gaps between 
                perception and reality, find new areas to focus engagement and communications, and 
                monitor how effectively you're changing how people feel — making the project a more 
                rewarding experience for everyone involved.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Eye, text: 'Discover hidden insights' },
                  { icon: Target, text: 'Focus engagement' },
                  { icon: TrendingUp, text: 'Monitor effectiveness' },
                  { icon: Users, text: 'Find stakeholders' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                    <div className="w-10 h-10 rounded-lg bg-[#5AA2AE]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#5AA2AE]" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#5AA2AE]/20 to-purple-500/20 rounded-3xl blur-3xl" />
              <PerceptionRealityCard />
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Anonymity Section */}
      <section className="py-20 px-6 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#5AA2AE]/30 to-purple-500/30 rounded-3xl blur-2xl" />
                <div className="relative bg-slate-800 rounded-2xl p-8 border border-slate-700">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#5AA2AE] flex items-center justify-center">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">Anonymous Response</h4>
                      <p className="text-sm text-slate-400">Identity protected</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {[
                      { label: 'Responses grouped', icon: Layers },
                      { label: 'AI normalized', icon: Brain },
                      { label: 'Identifiers redacted', icon: Lock },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                        <item.icon className="w-5 h-5 text-[#5AA2AE]" />
                        <span className="text-sm text-slate-300">{item.label}</span>
                        <CheckCircle className="w-4 h-4 text-emerald-400 ml-auto" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                How do we know people will be honest?
              </h2>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                Pulse is designed to preserve anonymity by grouping all responses and using AI to 
                normalize and redact any identifying features.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed">
                People can feel safe to share their thoughts and feelings without the fear of being 
                shamed for speaking their mind. This creates an environment of psychological safety 
                where honest feedback flows freely.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Not Just Another Survey Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            <span>More than a survey</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            This is not just another survey
          </h2>
          <p className="text-xl text-slate-600 mb-12 leading-relaxed max-w-3xl mx-auto">
            Pulse is a new way to manage ongoing stakeholder sentiment and perception — unlocking 
            a new understanding of the most critical element of project success: its people. 
            Promoting an improved project culture of collaboration and communication.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                icon: Activity, 
                title: 'Always On', 
                desc: 'Continuous sentiment monitoring, not point-in-time surveys' 
              },
              { 
                icon: GalaxyIcon, 
                title: 'Relationship Mapping', 
                desc: 'Discover hidden stakeholder networks and influences' 
              },
              { 
                icon: Brain, 
                title: 'AI-Powered', 
                desc: 'Intelligent insights that surface what matters most' 
              },
            ].map((item, idx) => (
              <div key={idx} className="p-6 rounded-2xl bg-gradient-to-b from-slate-50 to-white border border-slate-100 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 rounded-xl bg-[#5AA2AE]/10 flex items-center justify-center mb-4 mx-auto">
                  <item.icon className="w-7 h-7 text-[#5AA2AE]" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section - The Journey */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              The Path to Insight
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From project setup to actionable insights — here's how Pulse transforms stakeholder feedback into understanding.
            </p>
          </div>

          {/* Step 1: Project Configuration */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-[#5AA2AE] text-white flex items-center justify-center font-bold text-lg">
                1
              </div>
              <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Project Configuration
              </h3>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { 
                  icon: Target, 
                  title: 'Define Drivers', 
                  desc: 'Set the key drivers of engagement and sentiment you want to measure' 
                },
                { 
                  icon: Layers, 
                  title: 'Create Segments', 
                  desc: 'Define how you will categorize and group your stakeholders' 
                },
                { 
                  icon: ClipboardList, 
                  title: 'Build Questions', 
                  desc: 'Configure the questions and metrics you want to track' 
                },
                { 
                  icon: UserPlus, 
                  title: 'Initial Invites', 
                  desc: 'Identify and invite your first stakeholders to participate' 
                },
              ].map((item, idx) => (
                <div key={idx} className="relative">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 h-full">
                    <div className="w-12 h-12 rounded-lg bg-[#5AA2AE]/10 flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-[#5AA2AE]" />
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2">{item.title}</h4>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                  {idx < 3 && (
                    <div className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#5AA2AE] items-center justify-center shadow-md">
                      <ChevronRight className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step 2: User Journey Fork */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-[#5AA2AE] text-white flex items-center justify-center font-bold text-lg">
                2
              </div>
              <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Stakeholder Onboarding
              </h3>
            </div>
            
            {/* Fork Diagram */}
            <div className="relative">
              {/* Center line */}
              <div className="hidden md:block absolute left-1/2 top-0 w-0.5 h-16 bg-slate-200" />
              
              {/* Fork point */}
              <div className="flex justify-center mb-4">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                  <GitFork className="w-5 h-5 text-slate-500 rotate-180" />
                </div>
              </div>
              
              {/* Two Paths */}
              <div className="grid md:grid-cols-2 gap-8">
                {/* Path A: Admin Invitation */}
                <div className="relative">
                  <div className="hidden md:block absolute top-0 right-0 w-1/2 h-0.5 bg-slate-200" />
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <h4 className="font-bold text-slate-900">Path A: Invited by Admin</h4>
                    </div>
                    <p className="text-sm text-slate-600 mb-4">
                      Administrators invite specific stakeholders directly via email. 
                      Invitees receive a secure link to access their personalized portal.
                    </p>
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <UserCheck className="w-4 h-4" />
                      <span>Pre-verified access</span>
                    </div>
                  </div>
                </div>
                
                {/* Path B: Public Landing */}
                <div className="relative">
                  <div className="hidden md:block absolute top-0 left-0 w-1/2 h-0.5 bg-slate-200" />
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-purple-600" />
                      </div>
                      <h4 className="font-bold text-slate-900">Path B: Public Landing Page</h4>
                    </div>
                    <p className="text-sm text-slate-600 mb-4">
                      Anyone can access a public page to provide anonymous feedback. 
                      They can choose to identify themselves or remain anonymous.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-purple-600">
                        <Shield className="w-4 h-4" />
                        <span>Anonymous or self-identified</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Self-Identification Flow */}
              <div className="mt-8 flex justify-center">
                <ChevronDown className="w-6 h-6 text-slate-300" />
              </div>
              <div className="mt-4 max-w-xl mx-auto">
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-100">
                  <h4 className="font-bold text-slate-900 mb-3 text-center">Self-Identification & Verification</h4>
                  <div className="flex flex-wrap justify-center gap-4">
                    <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-sm">
                      <Mail className="w-4 h-4 text-slate-500" />
                      <span>Email verified</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
                    <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-sm">
                      <Eye className="w-4 h-4 text-slate-500" />
                      <span>Admin reviews</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
                    <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg text-sm">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      <span>Approved</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: About Me */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-[#5AA2AE] text-white flex items-center justify-center font-bold text-lg">
                3
              </div>
              <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                About Me — Share Your Perspective
              </h3>
            </div>
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Approved users access the <strong>About Me</strong> section — a clear way to provide insights 
                  using a variety of questions designed to measure sentiment in an "always on" way.
                </p>
                <ul className="space-y-3">
                  {[
                    'Go back and change responses at any time',
                    'All responses are anonymized for safety',
                    'Multiple question types: sliders, ratings, text',
                    'Progress tracking shows completion status',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#5AA2AE] mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <AboutMeQuestionsCard />
            </div>
          </div>

          {/* Step 4: About Others */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-[#5AA2AE] text-white flex items-center justify-center font-bold text-lg">
                4
              </div>
              <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                About Others — Map Your Network
              </h3>
            </div>
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="order-2 lg:order-1">
                <PeopleGalaxyCard />
              </div>
              <div className="order-1 lg:order-2">
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Selected users are invited to <strong>About Others</strong> — identifying stakeholders 
                  they work with, people or groups important to the project. They provide insights about 
                  their perception of these stakeholders.
                </p>
                <div className="space-y-4 mb-6">
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <h5 className="font-bold text-slate-900 mb-1 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500" />
                      Perception Matching
                    </h5>
                    <p className="text-sm text-slate-600">
                      Pulse matches perceived sentiment with actual sentiment provided by stakeholders themselves.
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <h5 className="font-bold text-slate-900 mb-1 flex items-center gap-2">
                      <GalaxyIcon className="w-4 h-4 text-purple-500" />
                      Network Discovery
                    </h5>
                    <p className="text-sm text-slate-600">
                      New stakeholders and groups are identified, building out a social network of relationships — the People Galaxy.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg text-sm">
                  <UserPlus className="w-4 h-4 text-[#5AA2AE]" />
                  <span className="text-slate-700">Newly identified stakeholders are reviewed by admins and may be invited to Pulse</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics & Insights Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#5AA2AE]/10 text-[#5AA2AE] text-sm font-semibold mb-6">
              <BarChart3 className="w-4 h-4" />
              <span>Powered by AI</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Analytics & Insights
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Pulse creates a variety of dashboards, analysis, and insights targeted at each type of user — 
              providing actionable recommendations for areas of focus and trend analysis over time.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: PieChart,
                title: 'Role-Based Dashboards',
                desc: 'Every user type gets customized views relevant to their needs and permissions.',
                color: 'from-blue-500 to-blue-600'
              },
              {
                icon: TrendingUp,
                title: 'Trend Analysis',
                desc: 'Track sentiment changes over time to measure the impact of your engagement efforts.',
                color: 'from-emerald-500 to-emerald-600'
              },
              {
                icon: Brain,
                title: 'AI Synthesis',
                desc: 'Leverage AI to synthesize the vast array of feedback and extract common themes.',
                color: 'from-purple-500 to-purple-600'
              },
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-6`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Sample Dashboard Preview */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Insights Dashboard
                </h3>
                <p className="text-sm text-slate-500">AI-powered recommendations</p>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#5AA2AE]/10 rounded-lg">
                <Sparkles className="w-4 h-4 text-[#5AA2AE]" />
                <span className="text-sm font-medium text-[#5AA2AE]">3 new insights</span>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm">Communication Improving</h4>
                      <p className="text-xs text-slate-600 mt-1">
                        Team communication scores increased 15% this month. Continue current engagement patterns.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm">Focus Area: Engineering</h4>
                      <p className="text-xs text-slate-600 mt-1">
                        Engineering segment shows perception gap. Recommended: increase cross-team visibility.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-bold text-slate-900 text-sm mb-4">Common Themes</h4>
                <div className="flex flex-wrap gap-2">
                  {[
                    { theme: 'Clear direction', count: 24 },
                    { theme: 'More updates', count: 18 },
                    { theme: 'Team support', count: 15 },
                    { theme: 'Resource needs', count: 12 },
                    { theme: 'Recognition', count: 9 },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-slate-200">
                      <span className="text-sm text-slate-700">{item.theme}</span>
                      <span className="text-xs text-slate-400">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#5AA2AE]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to put people first?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Start understanding your stakeholders better. Because people are everything.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-white text-[#5AA2AE] hover:bg-white/90 h-12 px-8 text-base"
              >
                Start 30-Day Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/">
              <Button 
                size="lg" 
                variant="outline"
                className="border-white text-white hover:bg-white/10 h-12 px-8 text-base"
              >
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/projectai-logo.png" alt="ProjectAI" className="h-8 object-contain" />
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Pulse by ProjectAI. People are everything.
          </p>
        </div>
      </footer>
    </div>
  );
}
